/*
 * Painted Idols — de cijferbalk telt op bij het scrollen.
 *
 * Willem, 02-09-2026: "het is wel leuk om hier animatie van optellen van de
 * getallen aan toe te voegen op scroll."
 *
 * Een eigen bestand en niet pi-motion.js: het optellen van een cijferbalk is
 * een losstaande feature, en de bewegingslaag draagt de doek-rotatie die daar
 * niets mee te maken heeft. Zo kunnen die twee onafhankelijk van elkaar
 * veranderen.
 *
 * Het raakt twee plekken die sinds 02-09-2026 dezelfde balk zijn:
 *   .usp-value        in de body van zeven pagina's (about, commissions,
 *                     erotic-art, faq en de drie topic-pagina's)
 *   .pi-proof__value  in sections/pi-proof.liquid, op de home en de collectie
 *
 * De getallen staan in de HTML en worden hier alleen overschreven terwijl de
 * animatie loopt. Valt dit bestand weg, staat prefers-reduced-motion aan, of
 * kent de browser IntersectionObserver niet — in alle drie de gevallen staat
 * de balk er compleet, met "350+" en al. Er is geen cijfer dat alleen door
 * script verschijnt (INTERACTIE.md §8.1).
 */
(function () {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const COUNT_MS = 900;
  const COUNT_SEL = '.usp-value, .pi-proof__value';
  /* §8.3: niets beweegt in de eerste 600 ms van de eerste render. */
  const QUIET_MS = 600;

  /**
   * Haalt het eerste getal uit een waarde en houdt de tekst eromheen apart.
   * De vier waarden in de winkel zijn "350+", "2022", "2023" en "2024–25",
   * dus er staat van alles voor en achter het getal.
   *
   * @param {string} text de zichtbare waarde
   * @returns {{pre: string, n: number, post: string}|null} null als er geen
   *   getal in staat — dan blijft de waarde onaangeroerd
   */
  function readNumber(text) {
    // De tekenklasse laat scheidingstekens toe: punt, komma, en de smalle en
    // gewone vaste spatie (U+202F, U+00A0). Als escape geschreven, want als
    // teken zijn die twee in de bron niet van een gewone spatie te zien.
    const m = text.match(/\d[\d.,\u202f\u00a0]*/);
    if (!m) return null;
    const digits = m[0].replace(/\D/g, '');
    // Boven negen cijfers is het geen feit meer maar een id; laat staan.
    if (!digits || digits.length > 9) return null;
    // A year stays still (Willem, 29-09-2026): 2022, 2023, 2024-25. "€1.600" has a separator and still counts.
    if (/^(19|20)\d\d$/.test(m[0])) return null;
    return {
      pre: text.slice(0, m.index),
      n: parseInt(digits, 10),
      post: text.slice(m.index + m[0].length),
    };
  }

  /**
   * Telt een waarde op van nul naar het getal dat er staat, en zet aan het eind
   * de oorspronkelijke tekst terug — niet een eigen opmaak ervan, zodat een
   * scheidingsteken of een streepje nooit verloren gaat.
   *
   * @param {HTMLElement} el het element met de waarde
   * @returns {void}
   */
  function countUp(el) {
    const original = el.textContent;
    const parsed = readNumber(original.trim());
    if (!parsed) return;

    // De breedte vasthouden zolang het telt. Het getal begint bij één cijfer en
    // eindigt op vier, en de cel zou anders meegroeien. Na afloop weer los,
    // want de maat is een clamp() en verandert dus met de vensterbreedte.
    const box = el.getBoundingClientRect();
    if (box.width) el.style.minWidth = box.width + 'px';

    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / COUNT_MS);
      // Uitlopend, zodat het laatste stuk leesbaar is in plaats van een flits.
      const eased = 1 - Math.pow(1 - p, 3);
      if (p < 1) {
        el.textContent = parsed.pre + Math.round(parsed.n * eased) + parsed.post;
        requestAnimationFrame(tick);
      } else {
        el.textContent = original;
        el.style.minWidth = '';
      }
    };
    requestAnimationFrame(tick);
  }

  /**
   * Zet het optellen klaar. Elke waarde telt eenmaal, zodra hij in beeld komt.
   *
   * @returns {void}
   */
  function init() {
    if (reduced) return; // de waarde staat er al, en dat is genoeg
    if (typeof IntersectionObserver !== 'function') return;

    const values = document.querySelectorAll(COUNT_SEL);
    if (!values.length) return;

    const t0 = performance.now();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          io.unobserve(entry.target);
          // Een balk die al in beeld staat wordt uitgesteld en niet
          // overgeslagen. Overslaan is wat init in pi-motion.js met
          // [data-pi-words] doet, maar daar staat de tekst dan al goed; hier
          // zou het getal op een kort scherm nooit tellen. Een observer vuurt
          // wél voor wat al in beeld staat — de eerste callback komt met
          // isIntersecting true — dus dit is een keuze en geen omweg om een
          // observer die niet afgaat.
          const wait = Math.max(0, QUIET_MS - (performance.now() - t0));
          setTimeout(() => countUp(entry.target), wait);
        }
      },
      { threshold: 0.5 }
    );
    values.forEach((el) => io.observe(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
